import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import ReportWhatsAppModal from '../../components/ReportWhatsAppModal';

const SaleReport = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [sales, setSales] = useState([]);
  const [totals, setTotals] = useState({});
  const [customers, setCustomers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(isAdmin ? 'ALL' : (user?.firmId || '1'));
  const [customerId, setCustomerId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAllFirms, setIsAllFirms] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [accRes, configRes] = await Promise.all([
          axiosClient.get('/accounts?type=CUSTOMER'),
          axiosClient.get('/auth/config')
        ]);
        if (accRes.success) setCustomers(accRes.data || []);
        if (configRes.success) setFirms(configRes.data?.firms || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchInitialData();
  }, []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const firmParam = isAdmin ? (selectedFirm || 'ALL') : (user?.firmId || '');
      const res = await axiosClient.get(
        `/reports/sales?customerId=${customerId}&fromDate=${fromDate}&toDate=${toDate}&firmId=${firmParam}`
      );
      if (res.success) {
        setSales(res.data || []);
        setTotals(res.totals || {});
        setIsAllFirms(res.isAllFirms || firmParam === 'ALL');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGenerate();
  }, [selectedFirm]);

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 no-print">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-graph-up-arrow text-primary me-2"></i> Sales Summary Report
          </h3>
          <p className="text-muted small mb-0">
            {isAdmin 
              ? 'Multi-Firm Admin Report: View sales across all firms or individual branches' 
              : `Sales Report for: ${user?.firmName || 'Assigned Branch'}`}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-success d-flex align-items-center gap-1"
            onClick={() => setShowWhatsAppModal(true)}
            disabled={sales.length === 0}
          >
            <i className="bi bi-whatsapp"></i> Send WhatsApp
          </button>
          <button className="btn btn-primary d-flex align-items-center gap-1" onClick={() => window.print()}>
            <i className="bi bi-printer me-1"></i> Print Report
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card shadow-sm border-0 mb-4 no-print">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            {isAdmin ? (
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted fw-semibold">
                  <i className="bi bi-building text-warning me-1"></i> Filter Firm Branch
                </label>
                <select
                  className="form-select"
                  value={selectedFirm}
                  onChange={(e) => setSelectedFirm(e.target.value)}
                >
                  <option value="ALL">★ All Firms (Consolidated)</option>
                  {firms.map((f) => (
                    <option key={f.Id} value={f.Id}>
                      {f.firmName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted fw-semibold">
                  <i className="bi bi-building text-warning me-1"></i> Your Branch
                </label>
                <div className="form-control bg-light text-dark fw-semibold small text-truncate">
                  {user?.firmName || 'Assigned Firm'}
                </div>
              </div>
            )}

            <div className="col-12 col-md-3">
              <label className="form-label small text-muted">Filter Customer</label>
              <select
                className="form-select"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">-- All Customers --</option>
                {customers.map((c) => (
                  <option key={c.Id} value={c.Id}>
                    {c.AccountName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label small text-muted">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label small text-muted">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-12 col-md-2">
              <button className="btn btn-ocean w-100" onClick={handleGenerate}>
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card p-3 shadow-sm border-0 bg-primary bg-opacity-10 border-start border-4 border-primary">
            <div className="text-muted small">TOTAL SALES VALUE</div>
            <h4 className="fw-bold text-primary mb-0">₹{parseFloat(totals.totalGrand || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card p-3 shadow-sm border-0 bg-success bg-opacity-10 border-start border-4 border-success">
            <div className="text-muted small">TOTAL PAID / COLLECTED</div>
            <h4 className="fw-bold text-success mb-0">₹{parseFloat(totals.totalPaid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card p-3 shadow-sm border-0 bg-danger bg-opacity-10 border-start border-4 border-danger">
            <div className="text-muted small">NET OUTSTANDING</div>
            <h4 className="fw-bold text-danger mb-0">₹{parseFloat(totals.totalBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card p-3 shadow-sm border-0 bg-warning bg-opacity-10 border-start border-4 border-warning">
            <div className="text-muted small">LOADING + ICE CHARGES</div>
            <h4 className="fw-bold text-dark mb-0">₹{(parseFloat(totals.totalHamali || 0) + parseFloat(totals.totalIce || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h4>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="card shadow-sm border-0 print-area">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 small">
              <thead className="table-light">
                <tr>
                  <th>Bill #</th>
                  <th>Date</th>
                  {isAllFirms && <th>Firm Branch</th>}
                  <th>Customer Name</th>
                  <th>Vehicle</th>
                  <th className="text-end">Subtotal</th>
                  <th className="text-end">Loading</th>
                  <th className="text-end">Ice</th>
                  <th className="text-end">Discount</th>
                  <th className="text-end">Grand Total</th>
                  <th className="text-end">Paid Amt</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isAllFirms ? 11 : 10} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </td>
                  </tr>
                ) : sales.length === 0 ? (
                  <tr>
                    <td colSpan={isAllFirms ? 11 : 10} className="text-center py-5 text-muted">No sales found for selected criteria.</td>
                  </tr>
                ) : (
                  sales.map((s) => (
                    <tr key={s.Id}>
                      <td><strong>#{s.SaleNo}</strong></td>
                      <td>{formatDate(s.SaleDate)}</td>
                      {isAllFirms && (
                        <td>
                          <span className="badge bg-light text-secondary border">{s.firmName || `Firm #${s.FirmId}`}</span>
                        </td>
                      )}
                      <td className="fw-bold text-dark">{s.CustomerName || 'Cash'}</td>
                      <td>{s.VehicleNo || '-'}</td>
                      <td className="text-end">₹{parseFloat(s.SubTotal || 0).toFixed(2)}</td>
                      <td className="text-end">₹{parseFloat(s.HamaliCharges || 0).toFixed(2)}</td>
                      <td className="text-end">₹{parseFloat(s.IceCharges || 0).toFixed(2)}</td>
                      <td className="text-end text-danger">-₹{parseFloat(s.DiscAmount || 0).toFixed(2)}</td>
                      <td className="text-end fw-bold text-primary">₹{parseFloat(s.GrandTotal || 0).toFixed(2)}</td>
                      <td className="text-end fw-semibold text-success">₹{parseFloat(s.PaidAmt || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* WhatsApp Sharing Modal */}
      {showWhatsAppModal && (
        <ReportWhatsAppModal
          show={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          title="Sales Summary Report"
          reportType="sales_report"
          reportData={{
            title: 'Sales Summary Report',
            subtitle: `Period: ${fromDate ? formatDate(fromDate) : 'Start'} to ${toDate ? formatDate(toDate) : 'Present'}`,
            fromDate,
            toDate,
            totals,
            columns: ['Bill #', 'Date', 'Customer', 'Vehicle', 'Grand Total', 'Paid Amt'],
            rows: sales.map(s => [
              `#${s.SaleNo}`,
              formatDate(s.SaleDate),
              s.CustomerName || 'Cash',
              s.VehicleNo || '-',
              `₹${parseFloat(s.GrandTotal || 0).toFixed(2)}`,
              `₹${parseFloat(s.PaidAmt || 0).toFixed(2)}`
            ]),
            summaryCards: [
              { label: 'Total Sales', value: `₹${parseFloat(totals.totalGrand || 0).toLocaleString('en-IN')}` },
              { label: 'Total Paid', value: `₹${parseFloat(totals.totalPaid || 0).toLocaleString('en-IN')}` },
              { label: 'Net Outstanding', value: `₹${parseFloat(totals.totalBalance || 0).toLocaleString('en-IN')}` }
            ],
            firmName: user?.firmName || 'PUSHPAI SEA FOOD(ALIBAG)'
          }}
        />
      )}
    </div>
  );
};

export default SaleReport;
